import {useState} from 'react';

import {usernameWithoutSpaces, generateWhatsAppUrl} from 'presentation/utils';
import {makeGetChatRoom} from 'main/factories/usecases';
import {makePostChatRoomV2} from 'main/factories/usecases/private/external-links/chat/post-chat-room-v2-factory';
import {makePostGraceBotEvent} from 'main/factories/usecases/private/my-payments-flow';
import {useHostName, usePersonalData} from 'presentation/hooks';

import {
	ChatRoomsEnum,
	ChatRoomsType,
	FeaturesEnum,
	OperationStateTypesEnum,
} from 'presentation/constants';
import {
	ChatV2Language,
	ChatV2TemplateName,
	ChatV2TransferReason,
	CHAT_V2_DEBT_NEGOTIATION_URL,
} from 'presentation/constants/chatV2Constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useAccountInformation} from './useAccountInformation';
import {useAccountDataStore} from '../store/useAccountDataStateStore';
import {useChatErrorSnackbarStore} from 'presentation/components/ErrorSnackbar/store/useChatErrorSnackbarStore';

export function useCallChatRoom(nmUsuario?: string) {
	const {beneficiary} = useAccountDataStore(store => store.accountData);
	const {healthOnly, odontoOnly} = useAccountInformation();
	const {currentPhone} = usePersonalData();
	const {dataHostNameState, isNotredameSaude} = useHostName();
	const {isEnabled: isFlagAppointment} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_CONTROLE_CHAT_AGENDAMENTO,
	);
	const {isEnabled: isDebtNegotiationHelp} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_DEBT_NEGOTIATION_HELP,
	);
	const {isEnabled: isBemobiGraceBotEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NEW_BILLS_BEMOBI_BOT,
	);

	const {chatRoom} = dataHostNameState;
	const [operationState, setOperationState] = useState<OperationStateTypesEnum>(
		OperationStateTypesEnum.NONE,
	);
	const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
	const setGlobalChatErrorOpen = useChatErrorSnackbarStore(state => state.setOpen);

	const CHAT_ROOMS: ChatRoomsType = {
		[ChatRoomsEnum.SAC]: chatRoom.SAC,
		[ChatRoomsEnum.SAC_ODONTO]: chatRoom.SAC_ODONTO,
		[ChatRoomsEnum.APP]: chatRoom.APP,
		[ChatRoomsEnum.EXAMS]: chatRoom.EXAMS,
		[ChatRoomsEnum.ODONTO]: chatRoom.ODONTO,
		[ChatRoomsEnum.DEBIT_NEGOTIATION]: chatRoom.DEBIT_NEGOTIATION,
		[ChatRoomsEnum.BANK_SLIP]: chatRoom.BANK_SLIP,
		[ChatRoomsEnum.BE_BORN_WELL]: chatRoom.C_NASCER_BEM,
	};

	const TRANSFERENCE_REASON: ChatRoomsType = {
		[ChatRoomsEnum.SAC]: ChatV2TransferReason.SAC_HEALTH,
		[ChatRoomsEnum.SAC_ODONTO]: ChatV2TransferReason.SAC_ODONTO,
		[ChatRoomsEnum.APP]: ChatV2TransferReason.APPOINTMENT_SCHEDULING,
		[ChatRoomsEnum.EXAMS]: ChatV2TransferReason.EXAMS_SCHEDULING,
		[ChatRoomsEnum.ODONTO]: ChatV2TransferReason.ODONTO,
		[ChatRoomsEnum.DEBIT_NEGOTIATION]: ChatV2TransferReason.BILLS,
		[ChatRoomsEnum.BANK_SLIP]: ChatV2TransferReason.BILLS,
		[ChatRoomsEnum.BE_BORN_WELL]: ChatV2TransferReason.APPOINTMENT_SCHEDULING,
	};

	function chatRoomsType(type?: ChatRoomsEnum) {
		if (type && CHAT_ROOMS[type]) {
			return CHAT_ROOMS[type];
		}

		if (healthOnly) {
			return chatRoom.APP;
		}
		if (odontoOnly) {
			return chatRoom.ODONTO;
		}

		return chatRoom.APP;
	}

	async function callChatRoom(type?: ChatRoomsEnum) {
		setShowErrorSnackbar(false);

		const civilName = usernameWithoutSpaces(beneficiary?.nmUsuarioC ?? '');
		const socialName = usernameWithoutSpaces(beneficiary?.nmSocialUsuarioC ?? '');
		const planCivilName = usernameWithoutSpaces(nmUsuario ?? '');

		const userName = socialName || civilName;
		const handleUserName = planCivilName || userName;
		const cdEmpresaPlano = beneficiary?.cdEmpresaPlano || 0;

		const bookingChannels = [
			ChatRoomsEnum.APP,
			ChatRoomsEnum.ODONTO,
			ChatRoomsEnum.EXAMS,
			ChatRoomsEnum.BE_BORN_WELL,
		].includes(type as ChatRoomsEnum);

		const isBookingOrEmptyAndHumanChat = (bookingChannels || !type) && isFlagAppointment;

		const additionalInformation = {
			contractNumber: String(beneficiary?.nuContratoC || '').padStart(7, '0'),
			cpfNumberC: String(beneficiary?.nuCpfC || '').padStart(11, '0'),
			companyPlanCode: cdEmpresaPlano,
			userCodeC: beneficiary?.cdUsuarioC || '',
			...(isBookingOrEmptyAndHumanChat
				? {
						motivoDatransferencia: TRANSFERENCE_REASON[type || ChatRoomsEnum.APP],
						codigoTransferencia: 'APLICATIVO',
					}
				: null),
		};

		try {
			setOperationState(OperationStateTypesEnum.SUBMITTING);

			if (type === ChatRoomsEnum.DEBIT_NEGOTIATION) {
				if (isBemobiGraceBotEnabled) {
					const graceBotResponse = await makePostGraceBotEvent()
						.post([
							{
								customerExternalId: (currentPhone ?? '').replace(/\D/g, ''),
								eventType: 'Assuntos_Financeiros',
								sentAt: new Date().toISOString(),
								metadata: {
									utm_source: 'PORTAL_BENEFICIARIO',
									planTypeC: beneficiary?.tipoPlanoC ?? '',
									companyPlanCode: String(cdEmpresaPlano),
									planName: beneficiary?.nmPlano ?? '',
									userCodeC: beneficiary?.cdUsuarioC ?? '',
									cpfNumberC: String(beneficiary?.nuCpfC ?? '').padStart(11, '0'),
									userNameC: beneficiary?.nmUsuarioC ?? '',
									contractNumberC: String(beneficiary?.nuContratoC ?? ''),
									dependentTypeC: beneficiary?.tipoUsuarioC ?? '',
								},
							},
						])
						.catch(() => null);

					if (graceBotResponse?.whatsapp) {
						const digits = graceBotResponse.whatsapp.replace(/\D/g, '');
						const phoneNumber = digits.startsWith('0')
							? `55${digits.slice(1)}`
							: digits;
						setOperationState(OperationStateTypesEnum.DONE);
						window.open(
							`https://api.whatsapp.com/send/?phone=${phoneNumber}&text=&type=phone_number&app_absent=0`,
							'_blank',
							'noopener,noreferrer',
						);
						return;
					}
				}

				if (isDebtNegotiationHelp) {
					setOperationState(OperationStateTypesEnum.DONE);
					window.open(CHAT_V2_DEBT_NEGOTIATION_URL);
					return;
				}

				const response = await makeGetChatRoom(false).get({
					roomType: chatRoomsType(type),
					nmUsuario: handleUserName ?? undefined,
				});
				setOperationState(OperationStateTypesEnum.DONE);
				window.open(response.url);
				return;
			}

			await makePostChatRoomV2().post({
				destinations: [
					{
						to: {
							phoneNumber: currentPhone || '',
						},
					},
				],
				whatsApp: {
					templateName: isBookingOrEmptyAndHumanChat
						? ChatV2TemplateName.APPOINTMENT_SCHEDULING
						: ChatV2TemplateName.CONTINUE_ATTENDANCE,
					templateData: [beneficiary?.nmUsuarioC || ''],
					language: ChatV2Language.PORTUGUESE_BRAZIL,
				},
				additionalInformation,
			});
			const whatsappUrl = generateWhatsAppUrl(cdEmpresaPlano, isNotredameSaude);
			setOperationState(OperationStateTypesEnum.DONE);
			window.open(whatsappUrl);
		} catch (error) {
			setOperationState(OperationStateTypesEnum.ERROR);
			setGlobalChatErrorOpen(true);
		}
	}

	const IS_LOADING = operationState === OperationStateTypesEnum.SUBMITTING;

	const handleCloseErrorSnackbar = () => {
		setShowErrorSnackbar(false);
	};

	return {
		callChatRoom,
		IS_LOADING,
		showErrorSnackbar,
		handleCloseErrorSnackbar,
	};
}
